import React from 'react';
import { toProperCase } from '../../lib/utils';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Badge } from '../ui/badge';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../ui/table';
import {
  Loader2, CheckCircle, XCircle, Pencil, Check, X, Package, Link2,
} from 'lucide-react';

export function DraftPartsTab({
  draftParts,
  loadingDraftParts,
  editingDraftPart,
  setEditingDraftPart,
  onEditDraftPart,
  onSaveDraftPart,
  onQuickApproveDraftPart,
  onRejectDraftPart,
}) {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Package className="h-5 w-5" />
          Draft Parts - Review & Approve
        </CardTitle>
        <CardDescription>
          AI-extracted parts from inquiries. Edit, approve to create parts, or reject.
        </CardDescription>
      </CardHeader>
      <CardContent>
        {loadingDraftParts ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : draftParts.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            <Package className="h-12 w-12 mx-auto mb-4 opacity-50" />
            <p>No pending draft parts</p>
            <p className="text-sm">Upload an inquiry document to extract parts</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Customer P/N</TableHead>
                  <TableHead>Description</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Grade</TableHead>
                  <TableHead>Qty</TableHead>
                  <TableHead>Match</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {draftParts.map((draft) => (
                  <TableRow key={draft.id} data-testid={`draft-part-${draft.id}`}>
                    <TableCell>
                      {editingDraftPart?.id === draft.id ? (
                        <Input
                          value={editingDraftPart.customer_part_no || ''}
                          onChange={(e) => setEditingDraftPart({
                            ...editingDraftPart,
                            customer_part_no: e.target.value
                          })}
                          className="w-32"
                        />
                      ) : (
                        <span className="font-mono text-sm">{draft.customer_part_no || '-'}</span>
                      )}
                    </TableCell>
                    <TableCell className="max-w-[200px]">
                      {editingDraftPart?.id === draft.id ? (
                        <Input
                          value={editingDraftPart.description || ''}
                          onChange={(e) => setEditingDraftPart({
                            ...editingDraftPart,
                            description: e.target.value
                          })}
                          onBlur={(e) => e.target.value && setEditingDraftPart(prev => ({
                            ...prev,
                            description: toProperCase(e.target.value)
                          }))}
                        />
                      ) : (
                        <span className="truncate block">{draft.description || '-'}</span>
                      )}
                    </TableCell>
                    <TableCell>
                      {editingDraftPart?.id === draft.id ? (
                        <Input
                          value={editingDraftPart.part_type || ''}
                          onChange={(e) => setEditingDraftPart({
                            ...editingDraftPart,
                            part_type: e.target.value
                          })}
                          className="w-24"
                        />
                      ) : (
                        String(draft.part_type || '-')
                      )}
                    </TableCell>
                    <TableCell>
                      {editingDraftPart?.id === draft.id ? (
                        <Input
                          value={editingDraftPart.grade || ''}
                          onChange={(e) => setEditingDraftPart({
                            ...editingDraftPart,
                            grade: e.target.value
                          })}
                          className="w-20"
                        />
                      ) : (
                        String(draft.grade || '-')
                      )}
                    </TableCell>
                    <TableCell>
                      {editingDraftPart?.id === draft.id ? (
                        <Input
                          type="number"
                          value={editingDraftPart.quantity || ''}
                          onChange={(e) => setEditingDraftPart({
                            ...editingDraftPart,
                            quantity: parseFloat(e.target.value) || null
                          })}
                          className="w-20"
                        />
                      ) : (
                        String(draft.quantity ?? '-')
                      )}
                    </TableCell>
                    <TableCell>
                      {draft.matched_existing_part_id ? (
                        <Badge variant="outline" className="bg-green-50 text-green-700">
                          <Link2 className="h-3 w-3 mr-1" />
                          Linked
                        </Badge>
                      ) : draft.similar_parts?.length > 0 ? (
                        <Badge variant="outline" className="bg-yellow-50 text-yellow-700">
                          Similar Found
                        </Badge>
                      ) : (
                        <Badge variant="outline">New</Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-1">
                        {editingDraftPart?.id === draft.id ? (
                          <>
                            <Button size="sm" variant="ghost" onClick={onSaveDraftPart}>
                              <Check className="h-4 w-4 text-green-600" />
                            </Button>
                            <Button size="sm" variant="ghost" onClick={() => setEditingDraftPart(null)}>
                              <X className="h-4 w-4 text-red-600" />
                            </Button>
                          </>
                        ) : (
                          <>
                            <Button size="sm" variant="ghost" onClick={() => onEditDraftPart(draft)}>
                              <Pencil className="h-4 w-4" />
                            </Button>
                            <Button 
                              size="sm" 
                              variant="ghost" 
                              className="text-green-600 hover:text-green-700 hover:bg-green-50"
                              onClick={() => onQuickApproveDraftPart(draft)}
                              data-testid={`approve-part-${draft.id}`}
                              title="Approve with Smart Copy"
                            >
                              <CheckCircle className="h-4 w-4" />
                            </Button>
                            <Button 
                              size="sm" 
                              variant="ghost" 
                              className="text-red-600 hover:text-red-700 hover:bg-red-50"
                              onClick={() => onRejectDraftPart(draft.id)}
                            >
                              <XCircle className="h-4 w-4" />
                            </Button>
                          </>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
